import apiClient from "../lib/api-client";

export interface BillingSession {
  id: string;
  tenantId: string;
  storeId: string;
  counterId?: string;
  store?: {
    id: string;
    name: string;
  };
  counter?: {
    id: string;
    name: string;
  };
  sessionNumber: string;
  sessionDate: string;
  shift: string;
  startTime: string;
  endTime?: string;
  status: "OPEN" | "CLOSED" | "RECONCILED";
  openedBy: string;
  openedByName?: string;
  closedBy?: string;
  closedByName?: string;
  openingCash: number;
  totalSales: number;
  totalReturns: number;
  totalCashIn: number;
  totalCashOut: number;
  totalCard: number;
  totalUPI: number;
  totalCredit: number;
  expectedCash?: number;
  actualCash?: number;
  variance?: number;
  invoiceCount: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    invoices: number;
  };
}

export interface CreateBillingSessionDto {
  storeId: string;
  counterId: string;
  shift: "MORNING" | "AFTERNOON" | "EVENING" | "NIGHT";
  openingCash: number;
  denominations: Array<{ denominationPaise: number; count: number }>;
}

export interface CloseBillingSessionDto {
  actualCash: number;
  notes?: string;
  denominations: Array<{ denominationPaise: number; count: number }>;
  reasonCode?: string;
  approvalRequestId?: string;
}

export interface CashDrawerCountDto {
  notes2000?: number;
  notes500?: number;
  notes200?: number;
  notes100?: number;
  notes50?: number;
  notes20?: number;
  notes10?: number;
  coins?: number;
  notes?: string;
}

export interface CashDrawer {
  id: string;
  sessionId: string;
  countType: "OPENING" | "CLOSING";
  notes2000: number;
  notes500: number;
  notes200: number;
  notes100: number;
  notes50: number;
  notes20: number;
  notes10: number;
  coins: number;
  totalAmount: number;
  countedBy: string;
  countedAt: string;
  notes?: string;
}

export interface BillingSessionResponse {
  data: BillingSession[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface SessionSummary {
  session: BillingSession;
  paymentBreakdown: Array<{
    paymentMethod: string;
    _sum: { totalAmount: number };
    _count: number;
  }>;
  totalSales: number;
  totalReturns: number;
  totalNet: number;
  invoiceCount: number;
  returnCount: number;
}

export interface HandoverCandidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface UnresolvedSessionPayment {
  id: string;
  saleId: string | null;
  customerId: string | null;
  method: string;
  status: "PENDING" | "AUTHORIZED" | "EXCESS_COLLECTION";
  appliedPaise: string;
  providerReference: string | null;
  createdAt: string;
  allowedActions: Array<
    | "CANCEL_PENDING"
    | "CAPTURE_AUTHORIZATION"
    | "CANCEL_AUTHORIZATION"
    | "RESOLVE_EXCESS_COLLECTION"
  >;
}

export interface HandoverSessionDto {
  countedCashPaise: number;
  denominations: Array<{ denominationPaise: number; count: number }>;
  handoverTo: string;
  reasonCode: string;
  approvalRequestId?: string;
}

function mutationKey(storageKey: string): string {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export const billingSessionService = {
  async openSession(data: CreateBillingSessionDto): Promise<BillingSession> {
    const storageKey = `medicorepay:billing-session:${data.storeId}:${data.counterId}:open-key`;
    const idempotencyKey = mutationKey(storageKey);
    const response = await apiClient.post("/billing-sessions/open", data, {
      headers: { "Idempotency-Key": idempotencyKey },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async closeSession(
    sessionId: string,
    data: CloseBillingSessionDto,
  ): Promise<BillingSession> {
    const storageKey = `medicorepay:billing-session:${sessionId}:close-key`;
    const idempotencyKey = mutationKey(storageKey);
    const response = await apiClient.post(
      `/billing-sessions/${sessionId}/close`,
      data,
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async getHandoverCandidates(sessionId: string): Promise<HandoverCandidate[]> {
    const response = await apiClient.get(
      `/payment-operations/sessions/${sessionId}/handover-candidates`,
    );
    return response.data.candidates;
  },

  async getUnresolvedPayments(
    sessionId: string,
  ): Promise<UnresolvedSessionPayment[]> {
    const response = await apiClient.get(
      `/payment-operations/sessions/${sessionId}/unresolved-attempts`,
    );
    return response.data.attempts;
  },

  async resolveSessionPayment(
    sessionId: string,
    attemptId: string,
    action: "CANCEL_PENDING" | "CANCEL_AUTHORIZATION",
  ): Promise<void> {
    await apiClient.post(
      `/payment-operations/attempts/${attemptId}/session-resolution`,
      { sessionId, action, reasonCode: "SESSION_CLOSE_CANCELLED" },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    );
  },

  async captureAuthorization(attemptId: string): Promise<void> {
    await apiClient.post(
      `/payment-operations/attempts/${attemptId}/capture`,
      {},
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    );
  },

  async resolveExcessCollection(
    attemptId: string,
    customerId: string,
    approvalRequestId: string,
  ): Promise<void> {
    await apiClient.post(
      `/payment-operations/attempts/${attemptId}/excess-resolution`,
      {
        decision: "CUSTOMER_CREDIT",
        customerId,
        reasonCode: "SESSION_CLOSE_EXCESS_COLLECTION",
        approvalRequestId,
      },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    );
  },

  async handoverSession(
    sessionId: string,
    data: HandoverSessionDto,
  ): Promise<BillingSession> {
    const storageKey = `medicorepay:billing-session:${sessionId}:handover-key`;
    const idempotencyKey = mutationKey(storageKey);
    const response = await apiClient.post(
      `/payment-operations/sessions/${sessionId}/reviews`,
      { reviewType: "HANDOVER", ...data },
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async getActiveSession(
    storeId: string,
    counterId?: string,
  ): Promise<BillingSession | null> {
    const searchParams = new URLSearchParams();
    if (counterId) searchParams.append("counterId", counterId);
    const response = await apiClient.get(
      `/billing-sessions/active/${storeId}?${searchParams}`,
    );
    return response.data;
  },

  async getAll(params?: {
    page?: number;
    limit?: number;
    storeId?: string;
    counterId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<BillingSessionResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.storeId) searchParams.append("storeId", params.storeId);
    if (params?.counterId) searchParams.append("counterId", params.counterId);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.startDate) searchParams.append("startDate", params.startDate);
    if (params?.endDate) searchParams.append("endDate", params.endDate);

    const response = await apiClient.get(`/billing-sessions?${searchParams}`);
    return response.data;
  },

  async getById(id: string): Promise<BillingSession> {
    const response = await apiClient.get(`/billing-sessions/${id}`);
    return response.data;
  },

  async getSessionSummary(sessionId: string): Promise<SessionSummary> {
    const response = await apiClient.get(
      `/billing-sessions/${sessionId}/summary`,
    );
    return response.data;
  },

  async recordCashDrawerCount(
    sessionId: string,
    countType: "OPENING" | "CLOSING",
    data: CashDrawerCountDto,
  ): Promise<CashDrawer> {
    const storageKey = `medicorepay:billing-session:${sessionId}:${countType}:cash-drawer-key`;
    const idempotencyKey = mutationKey(storageKey);
    const response = await apiClient.post(
      `/billing-sessions/${sessionId}/cash-drawer/${countType}`,
      data,
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },
};
